import { Component, input, AfterViewInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import type { ComposeOption } from 'echarts/core';
import type { BarSeriesOption } from 'echarts/charts';
import type {
  TooltipComponentOption,
  GridComponentOption,
  TitleComponentOption
} from 'echarts/components';

echarts.use([
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  CanvasRenderer
]);

type EChartsOption = ComposeOption<
  BarSeriesOption |
  TooltipComponentOption |
  GridComponentOption |
  TitleComponentOption
>;

interface IMonthlyData {
  currentYTD: {
    m: string;      // e.g., "January"
    v: number;      // monthly total (can be 0)
  }[];
}

@Component({
  selector: 'app-insights-ff-lcr-monthly-chart',
  standalone: false,
  templateUrl: './insights-ff-lcr-monthly-chart.component.html',
  styleUrl: './insights-ff-lcr-monthly-chart.component.css',
})
export class InsightsFfLcrMonthlyChartComponent implements AfterViewInit, OnDestroy {
  @ViewChild('chart') chartElement!: ElementRef<HTMLDivElement>;

  data = input.required<IMonthlyData>();

  private chartInstance!: echarts.ECharts;

  ngAfterViewInit(): void {
    const chartDom = this.chartElement.nativeElement;
    this.chartInstance = echarts.init(chartDom);

    const inputData = this.data();

    // Sort chronologically if not already (January → December)
    const monthOrder = [
      'January', 'February', 'March', 'April',
      'May', 'June', 'July', 'August',
      'September', 'October', 'November', 'December'
    ];

    const sortedData = [...inputData.currentYTD].sort((a, b) =>
      monthOrder.indexOf(a.m) - monthOrder.indexOf(b.m)
    );

    const xAxisData = sortedData.map(row => row.m);  // months: "January", "February", ...
    const seriesData = sortedData.map(row => row.v);

    const option: EChartsOption = {

      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params: any) => {
          const param = params[0];
          return `${param.name}<br/>Total: ${param.value}`;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',  // space for labels
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          rotate: 45,
          fontSize: 12,
          interval: 0
        }
      },
      yAxis: {
        type: 'value',
        name: 'Total'
      },
      series: [
        {
          name: 'Current YTD',
          type: 'bar',
          data: seriesData,
          itemStyle: {
            color: '#5470c6'
          },
          label: {
            show: true,
            position: 'top'
          }
        }
      ]
    };

    this.chartInstance.setOption(option);
  }

  ngOnDestroy(): void {
    this.chartInstance?.dispose();
  }
}
